// src/components/congressional/CommitteeViewTab.jsx
// Congressional Trade Tracker — Tab 3 (Committee View).
// Extracted from CongressPage.jsx — see TradeFeedTab.jsx's header for the
// full split rationale.
//
// THIS PASS ADDS (CONGRESSIONAL-PAGE-REDESIGN-DESIGN.md item 9): each
// committee row is now expandable to show its member roster (name, party,
// role) — congressMembers.js's /committees endpoint already returns this
// (added this same redesign pass). This closes the real, confirmed gap: no
// member names were reachable from this tab at all before. Each roster row
// links to the Member Detail Subpage.
//
// PARTIAL BUILD, still flagged honestly (unchanged from the original build):
// the design doc's full vision (Committee Oversight Heat Map with
// sector-overlap flagging, Committee-Level Track Record) depends on
// committee_sector_map/committee_trade_overlap/member_filing_lag_stats,
// none of which exist yet.

import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Crown } from 'lucide-react';
import { API_URL } from '../../styles/shared';
import ConFilterBar from './ConFilterBar';
import { PARTY_COLORS, Card, authHeaders } from './congressShared';

export default function CommitteeViewTab({ token, onNavigate }) {
  const [committees, setCommittees] = useState([]);
  const [chamber, setChamber] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(new Set()); // keys currently showing their roster

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const qs = chamber ? `?chamber=${chamber}` : '';
    try {
      const r = await fetch(API_URL + '/api/congress/committees' + qs, { headers: authHeaders(token) });
      const d = await r.json().catch(() => ({ success: false }));
      if (!d.success) setError(d.message || 'Could not load committee data.');
      else setCommittees(d.data.committees || []);
    } catch (e) { setError('Could not reach the server.'); }
    setLoading(false);
  }, [chamber, token]);

  useEffect(() => { load(); }, [load]);

  const toggleExpanded = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div>
      <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.25rem', color: '#f59e0b', fontSize: '0.8rem' }}>
        Committee membership + aggregate trade activity (2020+) only. Sector-overlap flagging and
        committee-level track records are coming soon.
      </div>

      <ConFilterBar
        showChamber chamber={chamber} onChamberChange={e => setChamber(e.target.value)}
      />

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '8px', padding: '0.75rem 1rem', color: '#ef4444', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</div>
      ) : committees.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No committee data available.</div>
      ) : committees.map((c, i) => {
        const key = `${c.committeeName}::${c.chamber}`;
        const isOpen = expanded.has(key);
        return (
          <Card key={i}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', cursor: 'pointer' }}
              onClick={() => toggleExpanded(key)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {isOpen ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#64748b" />}
                <span style={{ color: '#f1f5f9', fontWeight: 700 }}>{c.committeeName}</span>
                <span style={{ color: '#64748b', fontSize: '0.78rem', marginLeft: '0.5rem' }}>{c.chamber}</span>
              </div>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.78rem', color: '#94a3b8' }}>
                <span>{c.memberCount} members</span>
                {c.chairCount > 0 && <span style={{ color: '#ffd700' }}>{c.chairCount} chair</span>}
                <span style={{ color: '#00ff88', fontWeight: 700 }}>{c.tradeCount2020Plus} trades (2020+)</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {(c.members || []).map((m, mi) => (
                  <div
                    key={mi}
                    onClick={() => onNavigate('congressMember', { bioguideId: m.bioguideId, chamber: c.chamber })}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.35rem 0.25rem', fontSize: '0.82rem', cursor: 'pointer', borderRadius: '6px' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {m.committeeRole === 'CHAIR' && <Crown size={12} color="#ffd700" />}
                      <span style={{ color: '#f1f5f9' }}>{m.firstName} {m.lastName}</span>
                      <span style={{ color: PARTY_COLORS[m.party] || '#94a3b8', fontWeight: 700 }}>{m.party}</span>
                    </span>
                    <span style={{ color: m.committeeRole === 'CHAIR' ? '#ffd700' : '#64748b', fontSize: '0.75rem' }}>
                      {m.committeeRole} · {m.tradeCount2020Plus} trades
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
